import { GridItem, Skeleton } from "@chakra-ui/react"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import type { Asset, AssetHeadlines } from "@hydrologiq/gql-experience/graphql"
import { AssetKind, GetAssetHeadlinesDocument } from "@hydrologiq/gql-experience/graphql"
import { toTimestamp, nowDateTime, startOfDay, removeDays } from "@hydrologiq/common/helpers/date"
import { DateTime } from "timezonecomplete"
import NextRefuelFigure from "@hydrologiq/shared/figures/headlines/NextRefuelFigure"
import HydrogenLevelFigure from "@hydrologiq/shared/figures/headlines/HydrogenLevelFigure"
import HydrogenUsedFigure from "@hydrologiq/shared/figures/headlines/HydrogenUsedFigure"
import EnergyGeneratedFigure from "@hydrologiq/shared/figures/headlines/EnergyGeneratedFigure"
import TotalEmissionsFigure from "@hydrologiq/shared/figures/headlines/TotalEmissionsFigure"
import EmissionsFactorFigure from "@hydrologiq/shared/figures/headlines/EmissionsFactorFigure"
import CurrentLevelFigure from "./figures/CurrentLevelFigure"
import StateOfChargeFigure from "./figures/StateOfChargeFigure"
import CurrentPowerFigure from "@hydrologiq/shared/figures/headlines/CurrentPowerFigure"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"
import { FIGURE_KEYS, FIGURE_DEFAULTS, type FigureKey } from "@hydrologiq/shared/figures/figureKeys"
import CurrentOrder from "@hydrologiq/shared/figures/asset/CurrentOrder"
import Status from "@hydrologiq/shared/figures/asset/Status"
import DeployedAt from "@hydrologiq/shared/figures/asset/DeployedAt"
import NextService from "@hydrologiq/shared/figures/asset/NextService"
import { useGetFeature, useDepletionEnabled } from "@hydrologiq/shared/providers/helpers/useFeature"
import { useIsSectionEnabled } from "@hydrologiq/shared/providers/SectionFeatures"
import { useEffect, useMemo, useState } from "react"
import CustomerSiteFigure from "@hydrologiq/shared/figures/asset/CustomerSite"
import GeneratorRuntimeFigure from "@hydrologiq/shared/figures/headlines/GeneratorRuntimeFigure"
import HybridLoadFigure from "@hydrologiq/shared/figures/headlines/HybridLoadFigure"
import HybridFuelLevelFigure from "@hydrologiq/shared/figures/headlines/HybridFuelLevelFigure"
import HybridBatteryLevelFigure from "@hydrologiq/shared/figures/headlines/HybridBatteryLevelFigure"
import FuelSavedFigure from "@hydrologiq/shared/figures/headlines/FuelSavedFigure"

// Note: Figure visibility is now controlled by the backend via allowedFigures field

interface AssetHeadlineFiguresProps {
  asset?: Asset
  stop?: DateTime
  mode?: "overview" | "monitoring" // Default to "monitoring" for backwards compatibility
  refreshVersion?: number
  onMonitoringStatusChange?: () => void
}

const AssetHeadlineFigures = ({
  asset,
  stop,
  mode = "monitoring",
  refreshVersion,
  onMonitoringStatusChange,
}: AssetHeadlineFiguresProps) => {
  const sitesFeatureEnabled = useGetFeature("site_reservation")
  const depletionEnabled = useDepletionEnabled()
  const ordersEnabled = useIsSectionEnabled("orders")
  const showDeployedAt = sitesFeatureEnabled

  // Compute time boundaries in the user's browser timezone using timezonecomplete.
  // Keyed on today's date string so boundaries recalculate after midnight.
  const todayKey = new Date().toLocaleDateString()
  const runtimeBoundaries = useMemo(() => {
    const now = nowDateTime()
    const todayMidnight = startOfDay(now)

    // Yesterday: midnight-to-midnight local time
    const yesterdayStart = removeDays(todayMidnight, 1)
    const yesterdayStop = todayMidnight

    // Day before yesterday: for the trend comparison
    const dayBeforeStart = removeDays(todayMidnight, 2)
    const dayBeforeStop = yesterdayStart

    return {
      yesterdayStart: yesterdayStart.toIsoString(),
      yesterdayStop: yesterdayStop.toIsoString(),
      dayBeforeStart: dayBeforeStart.toIsoString(),
      dayBeforeStop: dayBeforeStop.toIsoString(),
      todayStart: todayMidnight.toIsoString(),
      todayStop: now.toIsoString(),
    }
  }, [todayKey])

  const { data, loading, error, refetch, networkStatus } = useQueryWithEndpoint(
    GetAssetHeadlinesDocument,
    {
      variables: {
        assetId: asset?.id ?? "",
        stop: stop ? toTimestamp(stop) : undefined,
        ...runtimeBoundaries,
      },
      skip: !asset?.id,
    },
    "primary" // Use the primary (experience API) endpoint
  )

  useEffect(() => {
    if (refreshVersion) refetch()
  }, [refreshVersion])

  const [headlines, setHeadlines] = useState<AssetHeadlines | null>(null)

  useEffect(() => {
    if (data?.getAssetHeadlines) {
      setHeadlines(data.getAssetHeadlines)
    }
  }, [data])

  // Build array of figures with their components
  const availableFigures = []
  const isOverviewMode = mode === "overview"
  const isHybridAsset = asset?.class?.toLowerCase() === "hybrid"
  const assetKindFigures = headlines?.allowedFigures || []
  // Helper function to check if a figure should be shown
  const shouldShowFigure = (figureKey: FigureKey) => {
    return assetKindFigures.includes(figureKey)
  }

  // Helper function to get fallback data for overview mode
  const getFallbackData = (data: any, fallback: any, metadata: any = {}) => {
    let headline = { ...(data || fallback) }
    if (headline && (!("metadata" in headline) || !headline.metadata)) {
      headline["metadata"] = metadata
    }
    return headline
  }

  if (headlines) {
    // Orders status (overview mode only, if enabled and required for this asset kind)
    if (isOverviewMode && ordersEnabled && assetKindFigures.includes("orders")) {
      availableFigures.push({
        key: "currentOrder",
        component: <CurrentOrder context={asset} />,
      })
      availableFigures.push({
        key: "customerSite",
        component: (
          <CustomerSiteFigure
            context={asset?.deployed_site ?? asset?.current_order?.site ?? undefined}
            customerName={asset?.deployed_customer?.name ?? asset?.current_order?.customer?.name ?? undefined}
          />
        ),
      })
    }

    // Asset status (replaces the old MonitoringStatus card; includes data connection indicator)
    const isParentAsset = Boolean(
      asset?.isParent ||
        asset?.class?.toLowerCase() === "hybrid" ||
        (asset as any)?.class_field?.toLowerCase() === "hybrid" ||
        asset?.kind?.toLowerCase() === "complex"
    )

    if (isOverviewMode || !isParentAsset) {
      availableFigures.push({
        key: "status",
        component: <Status context={asset as any} />,
      })
    }

    // Deployed at (overview mode only, if features enabled)
    if (isOverviewMode && showDeployedAt) {
      availableFigures.push({
        key: "deployedAt",
        component: <DeployedAt context={asset as any} />,
      })
    }

    // Run-dry (Asset Depleted in) - only for storage assets
    const storageKinds: AssetKind[] = [AssetKind.TubeTrailer, AssetKind.ManifoldCylinderPallet, AssetKind.DynamicStorage]
    const isStorageAsset = asset?.kind && storageKinds.includes(asset.kind as AssetKind)
    if (depletionEnabled && shouldShowFigure(FIGURE_KEYS.NEXT_REFUEL) && isStorageAsset) {
      const nextRefuelData = getFallbackData(headlines.nextRefuel, FIGURE_DEFAULTS[FIGURE_KEYS.NEXT_REFUEL])
      availableFigures.push({
        key: "nextRefuel",
        component: <NextRefuelFigure data={nextRefuelData} loading={loading} error={error?.message} />,
      })
    }

    // Hydrogen level
    if (shouldShowFigure(FIGURE_KEYS.HYDROGEN_LEVEL)) {
      const hydrogenLevelData = getFallbackData(headlines.hydrogenLevel, FIGURE_DEFAULTS[FIGURE_KEYS.HYDROGEN_LEVEL])
      availableFigures.push({
        key: "hydrogenLevel",
        component: <HydrogenLevelFigure data={hydrogenLevelData} loading={loading} error={error?.message} />,
      })
    }

    // Current level
    if (shouldShowFigure(FIGURE_KEYS.CURRENT_LEVEL)) {
      const currentLevelData = getFallbackData(headlines.currentLevel, FIGURE_DEFAULTS[FIGURE_KEYS.CURRENT_LEVEL])
      console.log(currentLevelData)
      availableFigures.push({
        key: "currentLevel",
        component: <CurrentLevelFigure data={currentLevelData} loading={loading} error={error?.message} />,
      })
    }
    // Hydrogen used (monitoring mode only)
    if (!isOverviewMode && shouldShowFigure(FIGURE_KEYS.HYDROGEN_USED)) {
      const hydrogenUsedData = getFallbackData(headlines.hydrogenUsed, FIGURE_DEFAULTS[FIGURE_KEYS.HYDROGEN_USED])
      availableFigures.push({
        key: "hydrogenUsed",
        component: <HydrogenUsedFigure data={hydrogenUsedData} loading={loading} error={error?.message} />,
      })
    }

    // Energy
    if (shouldShowFigure(FIGURE_KEYS.ENERGY)) {
      const energyData = getFallbackData(headlines.energy, FIGURE_DEFAULTS[FIGURE_KEYS.ENERGY])
      availableFigures.push({
        key: "energy",
        component: <EnergyGeneratedFigure data={energyData} loading={loading} error={error?.message} />,
      })
    }

    // Emissions factor (monitoring mode only)
    if (!isOverviewMode && shouldShowFigure(FIGURE_KEYS.EMISSIONS_FACTOR)) {
      const emissionsFactorData = getFallbackData(headlines.emissionsFactor, FIGURE_DEFAULTS[FIGURE_KEYS.EMISSIONS_FACTOR])
      availableFigures.push({
        key: "emissionsFactor",
        component: <EmissionsFactorFigure data={emissionsFactorData} loading={loading} error={error?.message} />,
      })
    }

    // CO2e (non-hybrid assets — rendered here; hybrid CO2e rendered after battery below)
    if (shouldShowFigure(FIGURE_KEYS.CO2E) && !isHybridAsset) {
      const co2eData = getFallbackData(headlines.co2e, FIGURE_DEFAULTS[FIGURE_KEYS.CO2E])
      availableFigures.push({
        key: "co2e",
        component: <TotalEmissionsFigure data={co2eData} loading={loading} error={error?.message} compact/>,
      })
    }

    // State of charge
    if (shouldShowFigure(FIGURE_KEYS.STATE_OF_CHARGE)) {
      const stateOfChargeData = getFallbackData(headlines.stateOfCharge, FIGURE_DEFAULTS[FIGURE_KEYS.STATE_OF_CHARGE])
      availableFigures.push({
        key: "stateOfCharge",
        component: <StateOfChargeFigure data={stateOfChargeData} loading={loading} error={error?.message} />,
      })
    }

    // Current power
    if (shouldShowFigure(FIGURE_KEYS.CURRENT_POWER)) {
      const currentPowerData = getFallbackData(headlines.currentPower, FIGURE_DEFAULTS[FIGURE_KEYS.CURRENT_POWER])
      availableFigures.push({
        key: "currentPower",
        component: <CurrentPowerFigure data={currentPowerData} loading={loading} error={error?.message} />,
      })
    }

    // --- Hybrid figures (ordered: Load > Fuel Level > Battery > Runtime > Fuel Saved) ---

    // Hybrid load (both modes — for hybrid parent assets)
    if (shouldShowFigure(FIGURE_KEYS.HYBRID_LOAD)) {
      const hybridLoadData = getFallbackData(headlines.hybridLoad, FIGURE_DEFAULTS[FIGURE_KEYS.HYBRID_LOAD])
      availableFigures.push({
        key: "hybridLoad",
        component: <HybridLoadFigure data={hybridLoadData} loading={loading} error={error?.message} />,
      })
    }

    // Hybrid fuel level (both modes — for hybrid parent assets)
    if (shouldShowFigure(FIGURE_KEYS.HYBRID_FUEL_LEVEL)) {
      const hybridFuelLevelData = getFallbackData(headlines.hybridFuelLevel, FIGURE_DEFAULTS[FIGURE_KEYS.HYBRID_FUEL_LEVEL])
      availableFigures.push({
        key: "hybridFuelLevel",
        component: <HybridFuelLevelFigure data={hybridFuelLevelData} loading={loading} error={error?.message} />,
      })
    }

    // Hybrid battery level (both modes — for hybrid parent assets)
    if (shouldShowFigure(FIGURE_KEYS.HYBRID_BATTERY_LEVEL)) {
      const hybridBatteryLevelData = getFallbackData(headlines.hybridBatteryLevel, FIGURE_DEFAULTS[FIGURE_KEYS.HYBRID_BATTERY_LEVEL])
      availableFigures.push({
        key: "hybridBatteryLevel",
        component: <HybridBatteryLevelFigure data={hybridBatteryLevelData} loading={loading} error={error?.message} />,
      })
    }

    // Generator runtime (for hybrid assets)
    if (shouldShowFigure(FIGURE_KEYS.GENERATOR_RUNTIME)) {
      const generatorRuntimeData = getFallbackData(headlines.generatorRuntime, FIGURE_DEFAULTS[FIGURE_KEYS.GENERATOR_RUNTIME])
      availableFigures.push({
        key: "generatorRuntime",
        component: <GeneratorRuntimeFigure data={generatorRuntimeData} loading={loading} error={error?.message} />,
      })
    }

    // Fuel saved (last — carousel with fuel litres / CO2e kg)
    if (shouldShowFigure(FIGURE_KEYS.HYBRID_FUEL_SAVED)) {
      const fuelSavedData = getFallbackData(headlines.hybridFuelSaved, FIGURE_DEFAULTS[FIGURE_KEYS.HYBRID_FUEL_SAVED])
      availableFigures.push({
        key: "hybridFuelSaved",
        component: <FuelSavedFigure data={fuelSavedData} loading={loading} error={error?.message} />,
      })
    }

    // Next service (overview mode only)
    if (isOverviewMode) {
      availableFigures.push({
        key: "nextService",
        component: <NextService context={asset as any} />,
      })
    }
  }

  // Show skeleton on initial loading or when query is skipped, but not on refetch
  const shouldShowSkeleton = networkStatus === 1 || !asset?.id || error || !headlines

  return shouldShowSkeleton ? (
    <Skeleton height="175px" width="100%" />
  ) : (
    <FigureGrid>
      {availableFigures.map(({ key, component }) => (
        <GridItem key={key}>{component}</GridItem>
      ))}
    </FigureGrid>
  )
}

export default AssetHeadlineFigures
